import { useEffect, useState } from "react";
import axios from "axios";
import ExcelExportButton from "../components/ExcelExportButton";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { DollarSign, Loader2, Save, TrendingUp, Package, Zap, Users, Box } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eur = (n) => (n == null ? "-" : `${Number(n).toFixed(4)} €`);
const pct = (n) => (n == null ? "-" : `${Number(n).toFixed(1)}%`);

export default function Costing() {
  const [summary, setSummary] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        axios.get(`${API}/costing/summary`),
        axios.get(`${API}/costing/config`),
      ]);
      setSummary(s.data); setCfg(c.data);
    } catch { toast.error("Erro"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveCfg = async () => {
    setSaving(true);
    try { await axios.put(`${API}/costing/config`, cfg); toast.success("Configuração guardada"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  if (loading || !summary) return <div className="flex justify-center p-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const t = summary.totals;

  return (
    <div className="space-y-4" data-testid="costing-page">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><DollarSign className="h-8 w-8 text-emerald-600" /> Custeio Industrial</h1>
          <p className="text-sm text-muted-foreground mt-1">Custo real por peça (MP + energia + MO + embalagem + overhead) e margens.</p>
        </div>
        <ExcelExportButton endpoint="costing" label="Exportar Excel" />
      </div>

      {/* Assumptions */}
      <div className="bg-white border rounded-lg p-4">
        <h2 className="font-semibold mb-3 flex items-center gap-2"><TrendingUp className="h-5 w-5" /> Pressupostos</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {["energy_kwh_price","machine_avg_kw","labor_hourly_cost","setup_flat_cost","overhead_pct"].map(k => (
            <div key={k}>
              <Label className="text-xs">{{
                energy_kwh_price: "Energia €/kWh",
                machine_avg_kw: "Máquina kW médio",
                labor_hourly_cost: "MO €/hora",
                setup_flat_cost: "Setup médio €",
                overhead_pct: "Overhead %",
              }[k]}</Label>
              <Input type="number" step="0.01" value={cfg?.[k] ?? 0}
                     onChange={e => setCfg({ ...cfg, [k]: parseFloat(e.target.value) || 0 })}
                     data-testid={`cfg-${k}`} />
            </div>
          ))}
        </div>
        <Button className="mt-3 bg-emerald-600 hover:bg-emerald-700" onClick={saveCfg} disabled={saving} data-testid="save-cfg-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Guardar
        </Button>
      </div>

      {/* Totals across all parts (average per-piece contribution) */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <TotalCard label="Material" value={eur(t.material / Math.max(1, summary.parts.length))} icon={Package} color="#0b1f3a" />
        <TotalCard label="Energia" value={eur(t.energy / Math.max(1, summary.parts.length))} icon={Zap} color="#f59e0b" />
        <TotalCard label="Mão-de-obra" value={eur(t.labor / Math.max(1, summary.parts.length))} icon={Users} color="#8b5cf6" />
        <TotalCard label="Embalagem" value={eur(t.packaging / Math.max(1, summary.parts.length))} icon={Box} color="#0ea5e9" />
        <TotalCard label="Overhead" value={eur(t.overhead / Math.max(1, summary.parts.length))} icon={TrendingUp} color="#64748b" />
        <TotalCard label="TOTAL médio" value={eur(t.total / Math.max(1, summary.parts.length))} icon={DollarSign} color="#16a34a" />
      </div>

      {/* Parts table */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="p-3 border-b font-semibold">Custo por peça ({summary.parts.length})</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-2">Código</th>
                <th className="text-left p-2">Descrição</th>
                <th className="text-left p-2">Cliente</th>
                <th className="text-right p-2">Material</th>
                <th className="text-right p-2">Energia</th>
                <th className="text-right p-2">MO</th>
                <th className="text-right p-2">Embal.</th>
                <th className="text-right p-2">Ovh.</th>
                <th className="text-right p-2 font-bold">Total</th>
                <th className="text-right p-2">Venda</th>
                <th className="text-right p-2">Margem</th>
              </tr>
            </thead>
            <tbody>
              {summary.parts.map((p, i) => {
                const marginColor = p.margin_pct == null ? "" : p.margin_pct >= 20 ? "text-emerald-600" : p.margin_pct >= 0 ? "text-amber-600" : "text-red-600";
                return (
                  <tr key={i} className="border-t hover:bg-slate-50" data-testid={`cost-row-${p.code}`}>
                    <td className="p-2 font-mono font-semibold">{p.code}</td>
                    <td className="p-2 text-xs">{p.description}</td>
                    <td className="p-2 text-xs">{p.client}</td>
                    <td className="p-2 text-right font-mono">{eur(p.material)}</td>
                    <td className="p-2 text-right font-mono">{eur(p.energy)}</td>
                    <td className="p-2 text-right font-mono">{eur(p.labor)}</td>
                    <td className="p-2 text-right font-mono">{eur(p.packaging)}</td>
                    <td className="p-2 text-right font-mono">{eur(p.overhead)}</td>
                    <td className="p-2 text-right font-mono font-bold">{eur(p.total)}</td>
                    <td className="p-2 text-right font-mono">{eur(p.sale_price)}</td>
                    <td className={`p-2 text-right font-mono font-bold ${marginColor}`}>{pct(p.margin_pct)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function TotalCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white border rounded-lg p-3 shadow-sm">
      <div className="text-[10px] text-slate-500 uppercase tracking-wide flex items-center gap-1"><Icon className="h-3 w-3" />{label}</div>
      <div className="text-lg font-bold mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}
